import React, { useState, useEffect } from 'react';
import { 
  Settings, Mail, MessageSquare, HardDrive, CreditCard, Database,
  Shield, Code, Zap, FileText, Clock, ToggleLeft, Save, RefreshCw,
  CheckCircle, AlertCircle, Plus, Trash2, Eye, EyeOff, Server,
  Activity, Bell, Lock, Globe, Download, Upload
} from 'lucide-react';
import { systemSettingsController } from '../../controllers/systemSettingsController';
import { authController } from '../../controllers/authController';

const SystemSettingsPage = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [activeTab, setActiveTab] = useState('app');
  const [isSaving, setSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState({});

  const [settings, setSettings] = useState({
    appName: 'TRACKLIO',
    appVersion: '1.0.0',
    maintenanceMode: false,
    maintenanceMessage: '',
    emailSettings: {
      smtpHost: '', smtpPort: 587, smtpUser: '', smtpPassword: '',
      fromEmail: '', fromName: '', enableSSL: true
    },
    smsSettings: {
      provider: 'none', apiKey: '', apiSecret: '', senderId: '', enabled: false
    },
    storageSettings: {
      provider: 'local', maxFileSize: 10485760, allowedFileTypes: [],
      bucketName: '', region: '', accessKey: '', secretKey: ''
    },
    paymentSettings: {
      enablePayments: false, currency: 'INR',
      stripe: { enabled: false, publishableKey: '', secretKey: '', webhookSecret: '' },
      razorpay: { enabled: false, keyId: '', keySecret: '' },
      paypal: { enabled: false, clientId: '', clientSecret: '', mode: 'sandbox' }
    },
    databaseSettings: {
      autoBackup: true, backupFrequency: 'daily', backupTime: '02:00', retentionDays: 30
    },
    securitySettings: {
      enableCaptcha: false, captchaProvider: 'recaptcha', captchaSiteKey: '', captchaSecretKey: '',
      maxLoginAttempts: 5, lockoutDuration: 30, enableRateLimiting: true,
      rateLimitRequests: 100, rateLimitWindow: 15
    },
    apiSettings: {
      enableAPI: true, apiVersion: 'v1', enableDocs: true, enableCORS: true, allowedOrigins: []
    },
    cacheSettings: {
      enableCache: true, cacheProvider: 'memory', cacheTTL: 3600,
      redisHost: '', redisPort: 6379, redisPassword: ''
    },
    loggingSettings: {
      enableLogging: true, logLevel: 'info', logToFile: true, logToDatabase: false,
      maxLogSize: 10485760, logRetentionDays: 30
    },
    sessionSettings: {
      sessionTimeout: 30, sessionProvider: 'memory', enableRememberMe: true, rememberMeDuration: 30
    },
    performanceSettings: {
      enableCompression: true, enableMinification: true, enableLazyLoading: true,
      enableCDN: false, cdnUrl: ''
    },
    featureFlags: {
      enableChat: true, enableNotifications: true, enableReports: true,
      enableExports: true, enableImports: true
    }
  });

  const [newOrigin, setNewOrigin] = useState('');
  const [newFileType, setNewFileType] = useState('');

  useEffect(() => {
    setCurrentUser(authController.getCurrentUser());
    const saved = systemSettingsController.getSettings();
    if (saved) setSettings(saved);
  }, []);

  const handleInputChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleNestedChange = (parent, field, value) => {
    setSettings(prev => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value }
    }));
  };

  const handleDeepNestedChange = (parent, child, field, value) => {
    setSettings(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [child]: { ...prev[parent][child], [field]: value }
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const success = systemSettingsController.updateSettings(settings);
      if (success) {
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleAddOrigin = () => {
    if (newOrigin.trim()) {
      systemSettingsController.addAllowedOrigin(newOrigin.trim());
      setSettings(prev => ({
        ...prev,
        apiSettings: {
          ...prev.apiSettings,
          allowedOrigins: [...prev.apiSettings.allowedOrigins, newOrigin.trim()]
        }
      }));
      setNewOrigin('');
    }
  };

  const handleRemoveOrigin = (origin) => {
    systemSettingsController.removeAllowedOrigin(origin);
    setSettings(prev => ({
      ...prev,
      apiSettings: {
        ...prev.apiSettings,
        allowedOrigins: prev.apiSettings.allowedOrigins.filter(o => o !== origin)
      }
    }));
  };

  const handleAddFileType = () => {
    if (newFileType.trim()) {
      systemSettingsController.addAllowedFileType(newFileType.trim());
      setSettings(prev => ({
        ...prev,
        storageSettings: {
          ...prev.storageSettings,
          allowedFileTypes: [...prev.storageSettings.allowedFileTypes, newFileType.trim()]
        }
      }));
      setNewFileType('');
    }
  };

  const handleRemoveFileType = (fileType) => {
    systemSettingsController.removeAllowedFileType(fileType);
    setSettings(prev => ({
      ...prev,
      storageSettings: {
        ...prev.storageSettings,
        allowedFileTypes: prev.storageSettings.allowedFileTypes.filter(ft => ft !== fileType)
      }
    }));
  };

  const handleExport = () => {
    const json = systemSettingsController.exportSettings();
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `system-settings-${new Date().toISOString()}.json`;
    a.click();
  };

  const handleImport = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const success = systemSettingsController.importSettings(event.target.result);
          if (success) {
            const saved = systemSettingsController.getSettings();
            setSettings(saved);
            alert('Settings imported successfully!');
          }
        } catch (error) {
          alert('Failed to import settings');
        }
      };
      reader.readAsText(file);
    }
  };

  const styles = {
    container: { padding: '24px', backgroundColor: '#f8fafc', minHeight: '100vh' },
    header: { marginBottom: '32px' },
    title: { fontSize: '32px', fontWeight: 'bold', color: '#1e293b', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '12px' },
    subtitle: { fontSize: '16px', color: '#64748b' },
    contentWrapper: { display: 'grid', gridTemplateColumns: '280px 1fr', gap: '24px' },
    sidebar: { backgroundColor: '#ffffff', borderRadius: '12px', padding: '16px', height: 'fit-content', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', position: 'sticky', top: '24px' },
    sidebarItem: { padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '12px', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s', marginBottom: '4px', fontSize: '14px', fontWeight: '500', color: '#64748b' },
    sidebarItemActive: { backgroundColor: '#eff6ff', color: '#3b82f6' },
    mainContent: { backgroundColor: '#ffffff', borderRadius: '12px', padding: '32px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
    sectionTitle: { fontSize: '20px', fontWeight: 'bold', color: '#1e293b', marginBottom: '24px', paddingBottom: '16px', borderBottom: '2px solid #e2e8f0' },
    formGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px', marginBottom: '24px' },
    formGroup: { display: 'flex', flexDirection: 'column', gap: '8px' },
    formGroupFull: { gridColumn: '1 / -1' },
    label: { fontSize: '14px', fontWeight: '600', color: '#1e293b' },
    input: { padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none' },
    select: { padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', backgroundColor: '#ffffff', cursor: 'pointer' },
    switchContainer: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', marginBottom: '12px' },
    switchLabel: { fontSize: '14px', fontWeight: '500', color: '#1e293b' },
    switch: { position: 'relative', width: '48px', height: '24px', backgroundColor: '#cbd5e1', borderRadius: '12px', cursor: 'pointer', transition: 'background-color 0.2s' },
    switchActive: { backgroundColor: '#3b82f6' },
    switchKnob: { position: 'absolute', top: '2px', left: '2px', width: '20px', height: '20px', backgroundColor: '#ffffff', borderRadius: '50%', transition: 'transform 0.2s' },
    switchKnobActive: { transform: 'translateX(24px)' },
    actionBar: { display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '32px', paddingTop: '24px', borderBottom: '2px solid #e2e8f0' },
    button: { padding: '12px 24px', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '8px' },
    saveButton: { backgroundColor: '#3b82f6', color: '#ffffff' },
    successMessage: { position: 'fixed', top: '24px', right: '24px', backgroundColor: '#10b981', color: '#ffffff', padding: '16px 24px', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', display: 'flex', alignItems: 'center', gap: '12px', zIndex: 1000 },
    listItem: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', backgroundColor: '#f8fafc', borderRadius: '8px', marginBottom: '8px' },
    deleteButton: { padding: '6px 12px', backgroundColor: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' },
    passwordInput: { position: 'relative' },
    passwordToggle: { position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer' }
  };

  if (!currentUser || currentUser.role !== 'super_admin') {
    return (
      <div style={styles.container}>
        <div style={{ backgroundColor: '#fee2e2', border: '2px solid #ef4444', borderRadius: '12px', padding: '24px', display: 'flex', alignItems: 'center', gap: '16px' }}>
          <AlertCircle size={32} color="#dc2626" />
          <div>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold', color: '#991b1b' }}>Access Denied</h3>
            <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#7f1d1d' }}>Only Super Admins can access System Settings.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {showSuccess && (
        <div style={styles.successMessage}>
          <CheckCircle size={24} />
          <span>Settings saved successfully!</span>
        </div>
      )}

      <div style={styles.header}>
        <h1 style={styles.title}>
          <Settings size={36} color="#3b82f6" />
          System Settings
        </h1>
        <p style={styles.subtitle}>Configure system-wide settings and integrations</p>
      </div>

      <div style={styles.contentWrapper}>
        <div style={styles.sidebar}>
          {[
            { id: 'app', label: 'Application', icon: Settings },
            { id: 'email', label: 'Email', icon: Mail },
            { id: 'sms', label: 'SMS', icon: MessageSquare },
            { id: 'storage', label: 'Storage', icon: HardDrive },
            { id: 'payment', label: 'Payment', icon: CreditCard },
            { id: 'database', label: 'Database', icon: Database },
            { id: 'security', label: 'Security', icon: Shield },
            { id: 'api', label: 'API', icon: Code },
            { id: 'cache', label: 'Cache', icon: Zap },
            { id: 'logging', label: 'Logging', icon: FileText },
            { id: 'session', label: 'Session', icon: Clock },
            { id: 'performance', label: 'Performance', icon: Activity },
            { id: 'features', label: 'Features', icon: ToggleLeft }
          ].map((tab) => (
            <div
              key={tab.id}
              style={{ ...styles.sidebarItem, ...(activeTab === tab.id && styles.sidebarItemActive) }}
              onClick={() => setActiveTab(tab.id)}
            >
              <tab.icon size={18} />
              {tab.label}
            </div>
          ))}
        </div>

        <div style={styles.mainContent}>
          {/* Application Settings */}
          {activeTab === 'app' && (
            <>
              <h2 style={styles.sectionTitle}>Application Settings</h2>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Application Name</label>
                  <input type="text" style={styles.input} value={settings.appName} onChange={(e) => handleInputChange('appName', e.target.value)} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Version</label>
                  <input type="text" style={styles.input} value={settings.appVersion} onChange={(e) => handleInputChange('appVersion', e.target.value)} />
                </div>
              </div>
              <div style={styles.switchContainer}>
                <div>
                  <div style={styles.switchLabel}>Maintenance Mode</div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Enable maintenance mode to prevent user access</div>
                </div>
                <div style={{ ...styles.switch, ...(settings.maintenanceMode && styles.switchActive) }} onClick={() => handleInputChange('maintenanceMode', !settings.maintenanceMode)}>
                  <div style={{ ...styles.switchKnob, ...(settings.maintenanceMode && styles.switchKnobActive) }} />
                </div>
              </div>
              {settings.maintenanceMode && (
                <div style={{ ...styles.formGroup, ...styles.formGroupFull }}>
                  <label style={styles.label}>Maintenance Message</label>
                  <input type="text" style={styles.input} value={settings.maintenanceMessage} onChange={(e) => handleInputChange('maintenanceMessage', e.target.value)} placeholder="System is under maintenance..." />
                </div>
              )}
              <div style={styles.formGrid}>
                <button style={{ ...styles.button, ...styles.saveButton }} onClick={handleExport}>
                  <Download size={16} /> Export Settings
                </button>
                <button style={{ ...styles.button, backgroundColor: '#10b981', color: '#ffffff' }} onClick={() => document.getElementById('importFile').click()}>
                  <Upload size={16} /> Import Settings
                  <input id="importFile" type="file" accept=".json" style={{ display: 'none' }} onChange={handleImport} />
                </button>
              </div>
            </>
          )}

          {/* Email Settings */}
          {activeTab === 'email' && (
            <>
              <h2 style={styles.sectionTitle}>Email Configuration</h2>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>SMTP Host</label>
                  <input type="text" style={styles.input} value={settings.emailSettings.smtpHost} onChange={(e) => handleNestedChange('emailSettings', 'smtpHost', e.target.value)} placeholder="smtp.gmail.com" />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>SMTP Port</label>
                  <input type="number" style={styles.input} value={settings.emailSettings.smtpPort} onChange={(e) => handleNestedChange('emailSettings', 'smtpPort', parseInt(e.target.value))} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>SMTP Username</label>
                  <input type="text" style={styles.input} value={settings.emailSettings.smtpUser} onChange={(e) => handleNestedChange('emailSettings', 'smtpUser', e.target.value)} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>SMTP Password</label>
                  <div style={styles.passwordInput}>
                    <input type={showPassword.smtp ? 'text' : 'password'} style={styles.input} value={settings.emailSettings.smtpPassword} onChange={(e) => handleNestedChange('emailSettings', 'smtpPassword', e.target.value)} />
                    <div style={styles.passwordToggle} onClick={() => setShowPassword({...showPassword, smtp: !showPassword.smtp})}>
                      {showPassword.smtp ? <EyeOff size={18} /> : <Eye size={18} />}
                    </div>
                  </div>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>From Email</label>
                  <input type="email" style={styles.input} value={settings.emailSettings.fromEmail} onChange={(e) => handleNestedChange('emailSettings', 'fromEmail', e.target.value)} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>From Name</label>
                  <input type="text" style={styles.input} value={settings.emailSettings.fromName} onChange={(e) => handleNestedChange('emailSettings', 'fromName', e.target.value)} />
                </div>
              </div>
              <div style={styles.switchContainer}>
                <div>
                  <div style={styles.switchLabel}>Enable SSL/TLS</div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Use secure connection for email</div>
                </div>
                <div style={{ ...styles.switch, ...(settings.emailSettings.enableSSL && styles.switchActive) }} onClick={() => handleNestedChange('emailSettings', 'enableSSL', !settings.emailSettings.enableSSL)}>
                  <div style={{ ...styles.switchKnob, ...(settings.emailSettings.enableSSL && styles.switchKnobActive) }} />
                </div>
              </div>
            </>
          )}

          {/* SMS Settings */}
          {activeTab === 'sms' && (
            <>
              <h2 style={styles.sectionTitle}>SMS Configuration</h2>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>SMS Provider</label>
                  <select style={styles.select} value={settings.smsSettings.provider} onChange={(e) => handleNestedChange('smsSettings', 'provider', e.target.value)}>
                    <option value="none">None</option>
                    <option value="twilio">Twilio</option>
                    <option value="nexmo">Nexmo</option>
                    <option value="msg91">MSG91</option>
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>API Key</label>
                  <input type="text" style={styles.input} value={settings.smsSettings.apiKey} onChange={(e) => handleNestedChange('smsSettings', 'apiKey', e.target.value)} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>API Secret</label>
                  <input type="password" style={styles.input} value={settings.smsSettings.apiSecret} onChange={(e) => handleNestedChange('smsSettings', 'apiSecret', e.target.value)} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Sender ID</label>
                  <input type="text" style={styles.input} value={settings.smsSettings.senderId} onChange={(e) => handleNestedChange('smsSettings', 'senderId', e.target.value)} />
                </div>
              </div>
              <div style={styles.switchContainer}>
                <div>
                  <div style={styles.switchLabel}>Enable SMS</div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Allow sending SMS notifications</div>
                </div>
                <div style={{ ...styles.switch, ...(settings.smsSettings.enabled && styles.switchActive) }} onClick={() => handleNestedChange('smsSettings', 'enabled', !settings.smsSettings.enabled)}>
                  <div style={{ ...styles.switchKnob, ...(settings.smsSettings.enabled && styles.switchKnobActive) }} />
                </div>
              </div>
            </>
          )}

          {/* Storage Settings */}
          {activeTab === 'storage' && (
            <>
              <h2 style={styles.sectionTitle}>Storage Configuration</h2>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Storage Provider</label>
                  <select style={styles.select} value={settings.storageSettings.provider} onChange={(e) => handleNestedChange('storageSettings', 'provider', e.target.value)}>
                    <option value="local">Local Storage</option>
                    <option value="aws_s3">AWS S3</option>
                    <option value="google_cloud">Google Cloud</option>
                    <option value="azure">Azure</option>
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Max File Size (bytes)</label>
                  <input type="number" style={styles.input} value={settings.storageSettings.maxFileSize} onChange={(e) => handleNestedChange('storageSettings', 'maxFileSize', parseInt(e.target.value))} />
                </div>
              </div>
              <div style={{ marginTop: '24px' }}>
                <label style={styles.label}>Allowed File Types</label>
                <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', marginTop: '8px' }}>
                  <input type="text" style={{ ...styles.input, flex: 1 }} value={newFileType} onChange={(e) => setNewFileType(e.target.value)} placeholder="Enter file extension (e.g., pdf)" />
                  <button style={{ ...styles.button, ...styles.saveButton }} onClick={handleAddFileType}>
                    <Plus size={16} /> Add
                  </button>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {settings.storageSettings.allowedFileTypes.map((ft, idx) => (
                    <div key={idx} style={{ padding: '8px 16px', backgroundColor: '#eff6ff', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '14px', fontWeight: '500', color: '#3b82f6' }}>.{ft}</span>
                      <Trash2 size={14} style={{ cursor: 'pointer', color: '#dc2626' }} onClick={() => handleRemoveFileType(ft)} />
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Feature Flags */}
          {activeTab === 'features' && (
            <>
              <h2 style={styles.sectionTitle}>Feature Flags</h2>
              <div style={styles.switchContainer}>
                <div>
                  <div style={styles.switchLabel}>Enable Chat</div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Real-time chat functionality</div>
                </div>
                <div style={{ ...styles.switch, ...(settings.featureFlags.enableChat && styles.switchActive) }} onClick={() => handleNestedChange('featureFlags', 'enableChat', !settings.featureFlags.enableChat)}>
                  <div style={{ ...styles.switchKnob, ...(settings.featureFlags.enableChat && styles.switchKnobActive) }} />
                </div>
              </div>
              <div style={styles.switchContainer}>
                <div>
                  <div style={styles.switchLabel}>Enable Notifications</div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Push notifications system</div>
                </div>
                <div style={{ ...styles.switch, ...(settings.featureFlags.enableNotifications && styles.switchActive) }} onClick={() => handleNestedChange('featureFlags', 'enableNotifications', !settings.featureFlags.enableNotifications)}>
                  <div style={{ ...styles.switchKnob, ...(settings.featureFlags.enableNotifications && styles.switchKnobActive) }} />
                </div>
              </div>
              <div style={styles.switchContainer}>
                <div>
                  <div style={styles.switchLabel}>Enable Reports</div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Advanced reporting features</div>
                </div>
                <div style={{ ...styles.switch, ...(settings.featureFlags.enableReports && styles.switchActive) }} onClick={() => handleNestedChange('featureFlags', 'enableReports', !settings.featureFlags.enableReports)}>
                  <div style={{ ...styles.switchKnob, ...(settings.featureFlags.enableReports && styles.switchKnobActive) }} />
                </div>
              </div>
              <div style={styles.switchContainer}>
                <div>
                  <div style={styles.switchLabel}>Enable Exports</div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Data export functionality</div>
                </div>
                <div style={{ ...styles.switch, ...(settings.featureFlags.enableExports && styles.switchActive) }} onClick={() => handleNestedChange('featureFlags', 'enableExports', !settings.featureFlags.enableExports)}>
                  <div style={{ ...styles.switchKnob, ...(settings.featureFlags.enableExports && styles.switchKnobActive) }} />
                </div>
              </div>
              <div style={styles.switchContainer}>
                <div>
                  <div style={styles.switchLabel}>Enable Imports</div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Data import functionality</div>
                </div>
                <div style={{ ...styles.switch, ...(settings.featureFlags.enableImports && styles.switchActive) }} onClick={() => handleNestedChange('featureFlags', 'enableImports', !settings.featureFlags.enableImports)}>
                  <div style={{ ...styles.switchKnob, ...(settings.featureFlags.enableImports && styles.switchKnobActive) }} />
                </div>
              </div>
            </>
          )}

          <div style={styles.actionBar}>
            <button style={{ ...styles.button, ...styles.saveButton }} onClick={handleSave} disabled={isSaving}>
              {isSaving ? <RefreshCw size={16} className="spin" /> : <Save size={16} />}
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemSettingsPage;